import { useLayoutEffect, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { Tabs } from '@gto/ui/tabs'
import { Typography } from '@gto/ui/typography'

import {
  useRedemptionsRedemptionOptionsCryptoList,
  useRedemptionsRedemptionOptionsSiteTransferList,
} from '@/api/gen'
import { IconEdgeCoin } from '@/assets/icons'
import { Loader } from '@/components/loader'
import { edgeCountToOneBucks } from '@/pages/rewards/constants'
import { redemptionListQueryOptions } from '@/pages/rewards/redemption-query-options'

import { CryptoRedeemForm } from './crypto-redeem-form'
import { RoomTransferForm } from './room-transfer-form'

export const RedemptionsTabs = () => {
  const intl = useIntl()
  const [activeTab, setActiveTab] = useState<'crypto' | 'room'>('crypto')
  const { data: siteOptions = [], isPending: isSitePending } =
    useRedemptionsRedemptionOptionsSiteTransferList({
      query: redemptionListQueryOptions,
    })
  const { data: cryptoOptions = [], isPending: isCryptoPending } =
    useRedemptionsRedemptionOptionsCryptoList({
      query: redemptionListQueryOptions,
    })

  const listsLoading = isSitePending || isCryptoPending
  const noData =
    !isSitePending && !isCryptoPending && siteOptions.length === 0 && cryptoOptions.length === 0

  useLayoutEffect(
    function selectAvailableTabWhenOptionsLoad() {
      if (listsLoading || noData) {
        return
      }
      if (cryptoOptions.length === 0 && siteOptions.length > 0) {
        setActiveTab('room')
      } else if (siteOptions.length === 0 && cryptoOptions.length > 0) {
        setActiveTab('crypto')
      }
    },
    [listsLoading, noData, cryptoOptions.length, siteOptions.length],
  )

  const onChangeTab = (value: 'crypto' | 'room') => {
    setActiveTab(value)
  }

  const renderTabContent = () => {
    if (noData) {
      return (
        <Typography
          color="text.secondary"
          variant="body3"
        >
          <FormattedMessage id="edge.premiumRedeem.noOptions" />
        </Typography>
      )
    }
    switch (activeTab) {
      case 'crypto':
        return <CryptoRedeemForm />

      case 'room':
        return <RoomTransferForm />
      default:
        return <CryptoRedeemForm />
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Typography variant="h6">
          <FormattedMessage id="edge.premiumRedeem.cashout" />
        </Typography>

        <div className="flex">
          <div className="flex items-center">
            <Typography
              className="font-semibold"
              color="info.light"
              component="span"
              variant="h6"
            >
              {edgeCountToOneBucks}{' '}
            </Typography>
            <IconEdgeCoin width={24} />
          </div>
          <Typography
            className="!text-nowrap font-semibold"
            color="text.secondary"
            variant="h6"
          >
            ={' '}
            <FormattedNumber
              currency="USD"
              style="currency"
              value={1}
            />
          </Typography>
        </div>
      </div>
      {listsLoading ? (
        <Loader />
      ) : (
        <>
          {!noData && (
            <Tabs.Root
              detached
              value={activeTab}
              onValueChange={onChangeTab}
            >
              <Tabs.List aria-label={intl.formatMessage({ id: 'edge.premiumRedeem.cashout' })}>
                {cryptoOptions.length > 0 && (
                  <Tabs.Tab value="crypto">
                    <FormattedMessage id="edge.premiumRedeem.cryptocurrency.title" />
                  </Tabs.Tab>
                )}

                {siteOptions.length > 0 && (
                  <Tabs.Tab value="room">
                    <FormattedMessage id="edge.premiumRedeem.roomTransfer.title" />
                  </Tabs.Tab>
                )}
              </Tabs.List>
            </Tabs.Root>
          )}
          {renderTabContent()}
        </>
      )}
    </div>
  )
}
